// 矩形圖層編輯器（SPEC #ls2505-334「編輯器」、DEV-06、W22）：VMS 設定頁「偵測區域」（editor: zones）與「隱私遮罩」
// （editor: privacy_masks）列點進來的全螢幕對話框；兩者都宣告時上方可切圖層，另一層半透明顯示（不能編）。
// 遮罩：上方二選一是「關 / 開」（privacy_mask_enabled），最多 4 個，其餘操作同偵測區域。
// 幾何與 codec 在 @kinio/web-sdk zones（跟 app/home-base web 的 ZoneEditor 同一套規則；VMS 這份多了 SPEC 要的
// 「全畫面 / 指定區域」二選一與「先快照、按〔看即時〕才開即時影像」）。
//
// - 上方二選一：全畫面 = 寫 zone_enabled = false、保留已畫的矩形（切回來還在）；指定區域 = 儲存時一起寫 zone_enabled = true 與 zones。
// - 矩形：空白處拖曳畫新的；點選後拖曳移動、拖四角縮放、Delete 刪除；標號 1–N（= zone_ids + 1）；最多 8 個，到上限提示。
//   放開時 < 5 × 5 的丟掉；寫出整數百分比、x + w ≤ 100、y + h ≤ 100。
// - 儲存：一次寫整個值（不逐個矩形寫）；存完 pending → 收斂後以 reported 重畫（設備丟掉不合法矩形時以回報為準、提示）。
//   「全部清除」= 空陣列 + zone_enabled = false。有未存改動時離開要確認。
// data-testid：zed_zone_editor、zed_surface、zone_rect_<n>、zone_handle_<nw|ne|sw|se>、zone_delete、zone_draft、zed_save、zed_clear、
// zed_close、zed_mode_full / zed_mode_zones、zone_limit_hint、zed_notice（data-kind）、zone_count（data-count）、zed_backdrop（data-state）、zed_live。
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_PRIVACY_MASKS,
  MAX_ZONES,
  drawnZone,
  moveZone,
  parsePrivacyMasks,
  parseZones,
  rectFromPoints,
  resizeZone,
  toPercent,
  zonesEqual,
  type Zone,
  type ZoneCorner,
} from '@kinio/web-sdk';
import { useConfirm } from '../../../components/ConfirmDialog';
import type { VmsDevice } from '../../../lib/devices';
import { Backdrop } from './Backdrop';
import { useBackdrop } from './useBackdrop';
import './zed.css';

/** 矩形圖層：偵測區域 / 隱私遮罩（ui.yaml editor）。 */
export type RectLayer = 'zones' | 'privacy_masks';

const LAYER = {
  zones: { max: MAX_ZONES, parse: parseZones },
  privacy_masks: { max: MAX_PRIVACY_MASKS, parse: parsePrivacyMasks },
} as const;

export interface ZoneValue {
  enabled: boolean;
  zones: Zone[];
}

/** 儲存結果：error = null 已收斂（reported / reportedEnabled = 收斂時設備回報的原值，以它重畫）；字串 = 失敗訊息，以目前的 reported 重畫。 */
export interface ZoneSaveResult {
  error: string | null;
  reported?: unknown;
  reportedEnabled?: boolean;
}

export interface ZoneEditorProps {
  dev: Pick<VmsDevice, 'apiEndpoint' | 'homeId' | 'aid'>;
  layer: RectLayer;
  title: string;
  /** reported 矩形原值（kinio_motion.zones / kinio_image.privacy_masks） */
  reportedZones: unknown;
  /** reported 開關（zone_enabled / privacy_mask_enabled）；沒宣告這個屬性 = undefined（只寫矩形） */
  reportedEnabled: boolean | undefined;
  /** 另一個圖層（兩者都宣告時）：半透明顯示、可切過去 */
  other?: { layer: RectLayer; label: string; rects: Zone[] };
  onSwitchLayer?: () => void;
  canSave: boolean;
  readOnlyReason?: string;
  onSave: (v: ZoneValue) => Promise<ZoneSaveResult>;
  onClose: () => void;
}

const DEFAULT_ASPECT = 16 / 9;
const CORNERS: ZoneCorner[] = ['nw', 'ne', 'sw', 'se'];

type Gesture =
  | { kind: 'draw'; x0: number; y0: number }
  | { kind: 'move'; index: number; orig: Zone; x0: number; y0: number }
  | { kind: 'resize'; index: number; orig: Zone; corner: ZoneCorner; x0: number; y0: number }
  | { kind: 'blocked' };

type Notice = { kind: 'limit' } | { kind: 'saved' } | { kind: 'reported' } | { kind: 'error'; text: string };

function replaceAt(list: Zone[], i: number, z: Zone): Zone[] {
  if (i < 0 || i >= list.length) return list;
  const cur = list[i];
  if (cur.x === z.x && cur.y === z.y && cur.w === z.w && cur.h === z.h) return list;
  const next = list.slice();
  next[i] = z;
  return next;
}

const rectStyle = (z: Zone): CSSProperties => ({ left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%` });

/** 刪除鈕放在選取矩形下方正中；下面放不下放上方；都放不下放中間。 */
function deleteButtonStyle(z: Zone): CSSProperties {
  const left = `${z.x + z.w / 2}%`;
  if (z.y + z.h <= 90) return { left, top: `${z.y + z.h}%`, transform: 'translate(-50%, 8px)' };
  if (z.y >= 10) return { left, top: `${z.y}%`, transform: 'translate(-50%, calc(-100% - 8px))' };
  return { left, top: `${z.y + z.h / 2}%`, transform: 'translate(-50%, -50%)' };
}

export function ZoneEditor({ dev, layer, title, reportedZones, reportedEnabled, other, onSwitchLayer, canSave, readOnlyReason, onSave, onClose }: ZoneEditorProps) {
  const { max, parse } = LAYER[layer];
  const mask = layer === 'privacy_masks';
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const alive = useRef(true);
  const backdrop = useBackdrop(dev);

  const propZones = useMemo(() => parse(reportedZones) ?? [], [parse, reportedZones]);
  // 沒宣告 zone_enabled 的設備：有矩形 = 指定區域、空陣列 = 全畫面（空陣列本來就是全畫面）
  const propEnabled = reportedEnabled ?? propZones.length > 0;
  const baseKey = JSON.stringify([propEnabled, propZones]);
  // 儲存收斂後的設備回報：設定查詢重抓回來之前先當基準（不然剛存完 dirty 還是 true）
  const [saved, setSaved] = useState<{ enabled: boolean; zones: Zone[] } | null>(null);
  const baseZones = saved?.zones ?? propZones;
  const baseEnabled = saved?.enabled ?? propEnabled;
  const baseRef = useRef({ enabled: baseEnabled, zones: baseZones });
  const [zones, setZones] = useState<Zone[]>(baseZones);
  const [enabled, setEnabled] = useState(baseEnabled);
  const [selected, setSelected] = useState<number | null>(null);
  const [preview, setPreview] = useState<Zone | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [saving, setSaving] = useState(false);
  const [aspect, setAspect] = useState(DEFAULT_ASPECT);
  const gesture = useRef<Gesture | null>(null);
  const savingRef = useRef(false);

  const editable = canSave && !saving;
  const drawing = editable && enabled;
  const dirty = enabled !== baseEnabled || !zonesEqual(zones, baseZones);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // 設備回報變了：沒有未存改動就以新的 reported 重畫；儲存中不動
  useEffect(() => {
    const prev = baseRef.current;
    baseRef.current = { enabled: propEnabled, zones: propZones };
    setSaved(null);
    if (savingRef.current) return;
    setZones((z) => (zonesEqual(z, prev.zones) ? propZones : z));
    setEnabled((e) => (e === prev.enabled ? propEnabled : e));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseKey 代表值；物件每次換參照
  }, [baseKey]);

  const requestClose = useCallback(async () => {
    if (savingRef.current) return;
    if (!dirty) return onClose();
    const ok = await ask({ danger: true, title: t('zed.discard_title'), body: t(mask ? 'zed.discard_body_masks' : 'zed.discard_body'), action: t('zed.discard') });
    if (ok) onClose();
  }, [dirty, onClose, ask, t]);

  const deleteSelected = useCallback(() => {
    if (selected === null || !drawing) return;
    setZones((d) => d.filter((_, i) => i !== selected));
    setSelected(null);
    setNotice(null);
  }, [selected, drawing]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector('[data-testid="confirm-dialog"]')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        void requestClose();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selected !== null) {
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose, deleteSelected, selected]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // ---- 指標 ----
  const pointOf = (e: { clientX: number; clientY: number }) => {
    const r = surfaceRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return { x: toPercent(e.clientX - r.left, r.width), y: toPercent(e.clientY - r.top, r.height) };
  };
  const capture = (e: ReactPointerEvent) => {
    try {
      surfaceRef.current?.setPointerCapture?.(e.pointerId);
    } catch {
      /* 合成事件 */
    }
  };
  const onSurfaceDown = (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setSelected(null);
    if (!drawing) return;
    capture(e);
    if (zones.length >= max) {
      gesture.current = { kind: 'blocked' };
      setNotice({ kind: 'limit' });
      return;
    }
    const p = pointOf(e);
    gesture.current = { kind: 'draw', x0: p.x, y0: p.y };
    setPreview(null);
  };
  const onRectDown = (index: number, e: ReactPointerEvent) => {
    e.stopPropagation();
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setSelected(index);
    if (!drawing) return;
    capture(e);
    const p = pointOf(e);
    gesture.current = { kind: 'move', index, orig: zones[index], x0: p.x, y0: p.y };
  };
  const onHandleDown = (index: number, corner: ZoneCorner, e: ReactPointerEvent) => {
    e.stopPropagation();
    if (!drawing) return;
    capture(e);
    const p = pointOf(e);
    gesture.current = { kind: 'resize', index, orig: zones[index], corner, x0: p.x, y0: p.y };
  };
  const apply = (g: Gesture, p: { x: number; y: number }) => {
    if (g.kind === 'move') setZones((d) => replaceAt(d, g.index, moveZone(g.orig, p.x - g.x0, p.y - g.y0)));
    else if (g.kind === 'resize') {
      const cx = (g.corner === 'ne' || g.corner === 'se' ? g.orig.x + g.orig.w : g.orig.x) + (p.x - g.x0);
      const cy = (g.corner === 'sw' || g.corner === 'se' ? g.orig.y + g.orig.h : g.orig.y) + (p.y - g.y0);
      setZones((d) => replaceAt(d, g.index, resizeZone(g.orig, g.corner, cx, cy)));
    }
  };
  const onMove = (e: ReactPointerEvent) => {
    const g = gesture.current;
    if (!g || g.kind === 'blocked') return;
    const p = pointOf(e);
    if (g.kind === 'draw') setPreview(rectFromPoints(g.x0, g.y0, p.x, p.y));
    else apply(g, p);
  };
  const onUp = (e: ReactPointerEvent, cancelled: boolean) => {
    const g = gesture.current;
    gesture.current = null;
    setPreview(null);
    if (!g || g.kind === 'blocked') return;
    const p = pointOf(e);
    if (g.kind === 'draw') {
      const z = cancelled ? null : drawnZone(g.x0, g.y0, p.x, p.y);
      if (!z || zones.length >= max) return;
      setNotice(null);
      setZones([...zones, z]);
      setSelected(zones.length);
      return;
    }
    if (!cancelled) apply(g, p);
    setNotice(null);
  };

  // ---- 儲存 ----
  const save = async () => {
    if (!canSave || !dirty || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setNotice(null);
    setSelected(null);
    const sent = { enabled, zones };
    let res: ZoneSaveResult;
    try {
      res = await onSave(sent);
    } catch (e) {
      res = { error: e instanceof Error ? e.message : String(e) };
    }
    savingRef.current = false;
    if (!alive.current) return;
    setSaving(false);
    if (res.error) {
      setZones(baseRef.current.zones);
      setEnabled(baseRef.current.enabled);
      setNotice({ kind: 'error', text: res.error });
    } else {
      const got = res.reported === undefined ? sent.zones : (parse(res.reported) ?? []);
      const gotEnabled = res.reportedEnabled ?? (reportedEnabled === undefined ? got.length > 0 : sent.enabled);
      setZones(got);
      setEnabled(gotEnabled);
      setSaved({ enabled: gotEnabled, zones: got });
      baseRef.current = { enabled: gotEnabled, zones: got };
      setNotice(zonesEqual(got, sent.zones) && gotEnabled === sent.enabled ? { kind: 'saved' } : { kind: 'reported' });
    }
  };
  const switchLayer = async () => {
    if (!onSwitchLayer || savingRef.current) return;
    if (dirty && !(await ask({ danger: true, title: t('zed.discard_title'), body: t(mask ? 'zed.discard_body_masks' : 'zed.discard_body'), action: t('zed.discard') }))) return;
    onSwitchLayer();
  };
  const clearAll = () => {
    if (!editable) return;
    setZones([]);
    setEnabled(false);
    setSelected(null);
    setNotice(null);
  };

  const count = zones.length;
  const sel = selected !== null && selected < zones.length ? zones[selected] : null;

  return (
    <div className="zed-overlay" role="dialog" aria-modal="true" aria-label={title} data-testid="zed_zone_editor" data-layer={layer}>
      <header className="zed-bar">
        <button type="button" className="btn" onClick={() => void requestClose()} disabled={saving} data-testid="zed_close">
          {t('zed.back')}
        </button>
        <h2 className="zed-title">{title}</h2>
        <div className="zed-actions">
          <button type="button" className="btn" onClick={clearAll} disabled={!editable || (count === 0 && !enabled)} data-testid="zed_clear">
            {t('zed.clear_all')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!canSave || !dirty || saving} data-testid="zed_save">
            {saving ? t('zed.saving') : t('common.save')}
          </button>
        </div>
      </header>

      {other && onSwitchLayer && (
        <div className="zed-layers" role="tablist" aria-label={t('zed.layers')}>
          <button type="button" role="tab" aria-selected="true" className="btn btn-sm" data-testid={`zed_layer_${layer}`}>
            {title}
          </button>
          <button type="button" role="tab" aria-selected="false" className="btn btn-sm" disabled={saving} onClick={() => void switchLayer()} data-testid={`zed_layer_${other.layer}`}>
            {other.label}
          </button>
        </div>
      )}
      <div className="zed-modes" role="radiogroup" aria-label={title}>
        <label>
          <input type="radio" name="zed-mode" checked={!enabled} disabled={!editable} onChange={() => setEnabled(false)} data-testid="zed_mode_full" />{' '}
          {t(mask ? 'zed.mask_mode_off' : 'zed.mode_full')}
        </label>
        <label>
          <input type="radio" name="zed-mode" checked={enabled} disabled={!editable} onChange={() => setEnabled(true)} data-testid="zed_mode_zones" />{' '}
          {t(mask ? 'zed.mask_mode_on' : 'zed.mode_zones')}
        </label>
        {!enabled && count > 0 && <span className="muted small">{t(mask ? 'zed.mask_kept_hint' : 'zed.kept_hint', { count })}</span>}
      </div>
      {mask && <p className="warn small zed-mask-warn">{t('zed.mask_warn')}</p>}

      <div className="zed-stage">
        <div
          ref={surfaceRef}
          className={`zed-media${drawing ? '' : ' zed-readonly'}${enabled ? '' : ' zed-off'}`}
          style={{ '--zed-ar': String(aspect) } as CSSProperties}
          role="application"
          aria-label={t('zed.surface')}
          data-testid="zed_surface"
          onPointerDown={onSurfaceDown}
          onPointerMove={onMove}
          onPointerUp={(e) => onUp(e, false)}
          onPointerCancel={(e) => onUp(e, true)}
        >
          <Backdrop b={backdrop} onAspect={setAspect} />
          {other?.rects.map((z, i) => (
            <div
              key={`o${i}`}
              className={`zed-rect zed-rect-other${other.layer === 'privacy_masks' ? ' zed-rect-mask' : ''}`}
              style={rectStyle(z)}
              aria-hidden="true"
              data-testid={`other_rect_${i + 1}`}
            />
          ))}
          {zones.map((z, i) => {
            const isSel = i === selected;
            return (
              <div
                key={i}
                className={`zed-rect${mask ? ' zed-rect-mask' : ''}${isSel ? ' zed-rect-sel' : ''}`}
                style={rectStyle(z)}
                role="button"
                aria-pressed={isSel}
                aria-label={t(mask ? 'zed.mask_label' : 'zed.zone_label', { n: i + 1 })}
                data-testid={`zone_rect_${i + 1}`}
                data-zone={`${z.x},${z.y},${z.w},${z.h}`}
                onPointerDown={(e) => onRectDown(i, e)}
              >
                <span className="zed-rect-label" aria-hidden="true">
                  {i + 1}
                </span>
                {isSel &&
                  drawing &&
                  CORNERS.map((c) => <span key={c} className={`zed-handle zed-handle-${c}`} onPointerDown={(e) => onHandleDown(i, c, e)} data-testid={`zone_handle_${c}`} />)}
              </div>
            );
          })}
          {sel && drawing && (
            <button
              type="button"
              className="zed-delete"
              style={deleteButtonStyle(sel)}
              aria-label={t(mask ? 'zed.mask_delete' : 'zed.delete', { n: (selected ?? 0) + 1 })}
              title={t(mask ? 'zed.mask_delete' : 'zed.delete', { n: (selected ?? 0) + 1 })}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={deleteSelected}
              data-testid="zone_delete"
            >
              ×
            </button>
          )}
          {preview && <div className="zed-rect zed-rect-draft" style={rectStyle(preview)} data-testid="zone_draft" />}
        </div>
      </div>

      <footer className="zed-foot">
        <span data-testid="zone_count" data-count={count}>
          {mask
            ? !enabled
              ? t('zed.mask_off')
              : count === 1
                ? t('zed.mask_count_one')
                : t('zed.mask_count', { count })
            : !enabled
              ? t('zed.full_frame')
              : count === 1
                ? t('zed.count_one')
                : t('zed.count', { count })}
          <span className="muted"> · {t('zed.limit_counter', { count, max })}</span>
        </span>
        {!canSave ? (
          <span className="muted" role="status" data-testid="zed_readonly">
            {readOnlyReason ?? t(mask ? 'zed.read_only_masks' : 'zed.read_only')}
          </span>
        ) : notice?.kind === 'limit' ? (
          <span className="warn" role="status" data-testid="zone_limit_hint">
            {t(mask ? 'zed.mask_limit' : 'zed.limit', { max })}
          </span>
        ) : notice ? (
          <span className={notice.kind === 'error' ? 'error' : notice.kind === 'reported' ? 'warn' : 'muted'} role="status" data-testid="zed_notice" data-kind={notice.kind}>
            {notice.kind === 'error' ? `${notice.text} ${t('zed.reported_updated')}` : notice.kind === 'saved' ? t('zed.saved') : t('zed.reported_updated')}
          </span>
        ) : (
          <span className="muted">{mask ? t(enabled ? 'zed.mask_hint' : 'zed.mask_hint_off') : t(enabled ? 'zed.hint' : 'zed.hint_full')}</span>
        )}
      </footer>
      {dialog}
    </div>
  );
}
